import React, { createRef } from 'react';
import ReactSwipe from 'react-swipe';
import classnames from 'classnames';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';

import s from './SwipeSlider.scss';

const MAX_CHILDREN_COUNT = 5;

export interface Props {
  indicatorsClassName?: string;
  goToNextInterval?: number; // ms
  children: React.ReactElement<any> | readonly React.ReactElement<any>[];
}

export interface State {
  activeIndex: number;
  stopRotation: boolean;
  mounted: boolean;
}

const SWIPE_STYLES: ReactSwipe.Style = {
  container: {
    overflow: 'hidden',
    position: 'relative',
  },
  wrapper: {
    overflow: 'hidden',
    position: 'relative',
  },
  child: {
    float: 'left',
    // we provide all this so ReactSwipe won't put it's own styles on the el
    // and we can override position and left on the child elements to snap
    // overall component height to the height of the active/shown element
    // see also: .active and .child css classes
  },
};

export class SwipeSlider extends React.Component<Props, State> {
  state = { activeIndex: 0, stopRotation: false, mounted: false };
  reactSwipeEl = createRef<ReactSwipe>();
  timer;

  componentDidMount() {
    this.setState({ mounted: true });
    if (this.props.goToNextInterval) {
      this.goToNextSlideAfterTimeout();
    }
  }

  componentDidUpdate() {
    this.goToNextSlideAfterTimeout();
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const items = SwipeSlider.getItems(props.children);
    if (state.activeIndex > items.length - 1) {
      return { activeIndex: items.length - 1 };
    }

    return null;
  }

  static getItems(children) {
    return React.Children.count(children) > MAX_CHILDREN_COUNT
      ? React.Children.toArray(children).slice(0, MAX_CHILDREN_COUNT)
      : React.Children.toArray(children);
  }

  goToNextSlideAfterTimeout = () => {
    clearTimeout(this.timer);
    if (this.state.stopRotation || !this.props.goToNextInterval) {
      return;
    }
    this.timer = setTimeout(() => {
      const items = SwipeSlider.getItems(this.props.children);
      if (items.length < 2) {
        return;
      }
      let nextIndex = this.state.activeIndex + 1;
      if (nextIndex >= items.length) {
        nextIndex = 0;
      }
      this.setState({ activeIndex: nextIndex });
      this.goToNextSlideAfterTimeout();
    }, this.props.goToNextInterval);
  };

  render() {
    const { indicatorsClassName, children } = this.props;
    const { activeIndex, mounted } = this.state;
    const items = SwipeSlider.getItems(children);
    return (
      <div className={classnames(s['root'], !mounted && s['not-mounted'])}>
        <ErrorBoundary debugComponent="SwipeSlider">
          <ReactSwipe
            ref={this.reactSwipeEl}
            swipeOptions={{
              startSlide: activeIndex,
              callback: this.onSlideChange,
            }}
            style={SWIPE_STYLES}
          >
            {items.map((child, index) => (
              <div
                key={child ? child['key'] : index}
                className={classnames(
                  s['child'],
                  index === activeIndex && s['active']
                )}
              >
                {child}
              </div>
            ))}
          </ReactSwipe>
          {items.length > 1 && (
            <div className={classnames(s['indicators'], indicatorsClassName)}>
              {items.map((_, index) => (
                <div
                  key={index}
                  className={classnames(
                    s['indicator'],
                    index === activeIndex && s['active']
                  )}
                  onClick={() => {
                    this.setState(
                      { activeIndex: index, stopRotation: true },
                      () =>
                        this.reactSwipeEl.current &&
                        this.reactSwipeEl.current.slide(
                          this.state.activeIndex,
                          0
                        )
                    );
                  }}
                />
              ))}
            </div>
          )}
        </ErrorBoundary>
      </div>
    );
  }

  onSlideChange = index => {
    if (!this.props.goToNextInterval) {
      this.setState({ activeIndex: index });
    }
  };
}

export default SwipeSlider;
